import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';

import {
  Client,
  MeterEventLogQueryDataIn,
  PageInfo,
  PermissionsEnum,
  QueryScheduledDataReadDataIn,
  QueryScheduledDataReadDataOut,
  SortingType,
} from '../../../api/api-reference';
import { generateFileNameAppendix } from '../../../helper/download-filename';
import { AuthService } from '../../../services/auth.service';
import { FileService } from '../../../services/file.service';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { SearchComponent } from '../../core/components/search/search.component';
import { DateRangeInput } from '../../core/models/date-range-input';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { ClipboardService } from '../../../services/clipboard.service';
import { processDateRange } from '../../../helper/date-utils';
import { ScheduledDataFilterComponent, ScheduledDataFilterOutput } from '../../core/components/table/filter-panel/filters/scheduled-data-filter/scheduled-data-filter.component';
import { IsNewDirective } from '../../../directives/is-new.directive';

@Component({
  selector: 'app-scheduled-event-logs',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AngularSvgIconModule,
    DatepickerComponent,
    SearchComponent,
    PaginationComponent,
    ScheduledDataFilterComponent,
    IsNewDirective,
  ],
  templateUrl: './scheduled-event-logs.component.html',
  styleUrl: './scheduled-event-logs.component.scss',
})
export class ScheduledEventLogsComponent {
  public PermissionsEnum = PermissionsEnum;
  readonly dayInMs = 24 * 60 * 60 * 1000;
  public currentPage: number = 1;
  public numberOfDevices: number = 0;
  public lastPageSize: number = 1;
  public pageSize: number = 10;
  isDatepickerOpen = false;
  selectedDate: string | null = '';
  data: QueryScheduledDataReadDataOut[] = [];
  requestData = new QueryScheduledDataReadDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: 1 }),
    startDate: undefined,
    endDate: undefined,
    search: undefined,
    sorting: SortingType.Descending,
    assetTypes: [],
  });
  selectedFilters: ScheduledDataFilterOutput = {
    selectedAssetTypes: [],
    areas: [],
    regions: [],
  };

  constructor(
    private client: Client,
    private toastr: ToastrService,
    private router: Router,
    public authService: AuthService,
    private fileService: FileService,
    public clipboardService: ClipboardService,
  ) {
    this.reloadTable(1);
  }

  onDataReceived(event: ScheduledDataFilterOutput) {
    this.selectedFilters = event;
    this.requestData.assetTypes = event.selectedAssetTypes;
    this.requestData.areas = event.areas;
    this.requestData.regions = event.regions;
    this.reloadTable(1);
  }

  // Downloads every scheduled event of the meter inside the selected date range.
  download(meterId: string) {
    const fileNameApendix = generateFileNameAppendix();
    const data = new MeterEventLogQueryDataIn({
      meterId,
      startDate: this.requestData.startDate,
      endDate: this.requestData.endDate,
      sorting: SortingType.Descending,
      pageInfo: new PageInfo({ pageSize: 0, page: 0 }),
    });
    this.client.downloadMeterEventLogs(data).subscribe({
      next: (response) => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `EventLogs${fileNameApendix}`,
        );
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  toggleModal() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.reloadTable(1);
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable(1);
  }

  reloadTable(page: number) {
    this.currentPage = page;
    this.requestData.pageInfo!.page = page;

    this.client.queryScheduledEventLogDevices(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data!;
        this.numberOfDevices = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfDevices / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  pageChange(value: number) {
    this.reloadTable(value);
  }

  rowClick(meterId: string) {
    this.router.navigate(['/scheduled/event-logs-details'], {
      queryParams: { meterId },
    });
  }
}
